import { Note, Project, Category, Tag } from '../../../_service/data.service';
import { FilterParams } from '../../../_service/filter.service';
import Delta from 'quill-delta';

/**
 * Pure filter logic extracted from HomeComponent.
 * Contains stateless functions for filtering, searching, and note categorization.
 */
export class FilterLogic {
  private static normalizeSearchableText(value: string | null | undefined): string {
    return String(value ?? '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  private static stripWhitespace(value: string): string {
    return value.replace(/\s+/g, '');
  }

  private static htmlToPlainText(html: string | null | undefined): string {
    if (!html) {
      return '';
    }

    // Lightweight conversion suitable for search indexing in both browser and test environments.
    return html
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private static isValidDate(value: unknown): value is Date {
    return value instanceof Date && !Number.isNaN(value.getTime());
  }

  private static noteMatchesFullText(note: Note, searchText: string): boolean {
    const normalizedSearch = FilterLogic.normalizeSearchableText(searchText);
    if (!normalizedSearch) {
      return true;
    }

    const rawDescriptionHtml = FilterLogic.normalizeSearchableText(note.DescriptionHtml);
    const rawCommentsHtml = FilterLogic.normalizeSearchableText(note.CommentsHtml);
    const plainDescription = FilterLogic.normalizeSearchableText(
      FilterLogic.htmlToPlainText(note.DescriptionHtml),
    );
    const plainComments = FilterLogic.normalizeSearchableText(
      FilterLogic.htmlToPlainText(note.CommentsHtml),
    );

    // Also support words broken by inline HTML tags by comparing whitespace-stripped forms.
    const compactSearch = FilterLogic.stripWhitespace(normalizedSearch);
    const searchableValues = [
      FilterLogic.normalizeSearchableText(note.Title),
      FilterLogic.normalizeSearchableText(note.Keywords),
      FilterLogic.normalizeSearchableText(note.AiKeywords),
      rawDescriptionHtml,
      rawCommentsHtml,
      plainDescription,
      plainComments,
    ];

    return searchableValues.some((value) => {
      if (!value) {
        return false;
      }
      return value.includes(normalizedSearch) || FilterLogic.stripWhitespace(value).includes(compactSearch);
    });
  }

  private static noteMatchesTagSearch(note: Note, searchText: string, tagById: Map<string, Tag>): boolean {
    const tags = note.Tags;
    if (!Array.isArray(tags) || !tags.length) {
      return false;
    }

    return tags.some((tag) => {
      if (typeof tag === 'string') {
        const resolvedTag = tagById.get(tag);
        if (resolvedTag) {
          const resolvedValues = [resolvedTag.name, resolvedTag.slug, resolvedTag.tagType, resolvedTag._id];
          return resolvedValues.some((value) => String(value ?? '').toLowerCase().includes(searchText));
        }
        return tag.toLowerCase().includes(searchText);
      }

      const values = [tag.name, tag.slug, tag.tagType, tag._id];
      return values.some((value) => String(value ?? '').toLowerCase().includes(searchText));
    });
  }

  /**
   * Convert Quill Delta to plain text
   */
  static deltaToPlainText(delta: Delta | null | undefined): string {
    if (!delta) return '';
    return delta.ops.map((op: any) => (typeof op.insert === 'string' ? op.insert : '')).join('');
  }

  /**
   * Find a category and all its descendants within the hierarchy
   */
  static getCategoryWithChildren(categoryID: string, categories: Category[]): Category[] {
    // Find the main category record
    const mainCategory = categories.find((cat) => cat._id === categoryID);
    if (!mainCategory) {
      return []; // Return empty array if category not found
    }

    // Find all categories that fall within the LeftValue and RightValue range
    return categories.filter(
      (cat) => cat.LeftValue >= mainCategory.LeftValue && cat.RightValue <= mainCategory.RightValue,
    );
  }

  /**
   * Generate a human-readable time prefix (e.g., "3m: " for 3 months ago)
   */
//   static getNoteYearPrefix(note: Note): string {
//     const now = new Date();
//     const modified = new Date(note.last_modified);

//     const yearsDiff = now.getFullYear() - modified.getFullYear();
//     const monthsDiff = now.getMonth() - modified.getMonth();
//     let totalMonths = yearsDiff * 12 + monthsDiff;

//     // If current day is less than the modified day, subtract 1 (not full month yet)
//     if (now.getDate() < modified.getDate()) {
//       totalMonths = totalMonths - 1;
//     }

//     if (totalMonths) {
//       return `${totalMonths}m: `;
//     } else {
//       return '';
//     }
//   }

  /**
   * Get all notes for a project, sorted by modification date (newest first)
   */
  static getLastModifiedNotes(projectId: number, filteredNotes: Note[]): Note[] {
    const notes = filteredNotes.filter((note) => note.Project === projectId);
    return notes.sort((a, b) => {
      const dateA = new Date(a.last_modified);
      const dateB = new Date(b.last_modified);
      return dateB.getTime() - dateA.getTime(); // Descending order
    });
  }

  /**
   * Count how many filtered notes belong to a project
   */
  static calculateItemCount(projectId: number, filteredNotes: Note[]): number {
    return filteredNotes.filter((note) => note.Project === projectId).length;
  }

  /**
   * Apply all active filters to the note list
   */
  static applyFilters(
    allNotes: Note[],
    filter: FilterParams,
    projects: Project[],
    categories: Category[],
    availableTags: Tag[] = [],
  ): Note[] {
    // console.log('Applying filters:', filter);
    let filteredData = allNotes;
    const tagById = new Map<string, Tag>(availableTags.map((tag) => [tag._id, tag]));

    if (Array.isArray(filter.TagIds) && filter.TagIds.length > 0) {
      const requiredTagIds = new Set(filter.TagIds.filter((tagId) => !!tagId));
      const includeMode = filter.TagIncludeMode === 'OR' ? 'OR' : 'AND';
      filteredData = filteredData.filter((note) => {
        const tags = Array.isArray(note.Tags) ? note.Tags : [];
        if (!tags.length) {
          return false;
        }

        const noteTagIds = new Set(
          tags.map((tag) => (typeof tag === 'string' ? tag : String(tag._id ?? '')))
        );
        if (includeMode === 'OR') {
          return [...requiredTagIds].some((id) => noteTagIds.has(id));
        }
        return [...requiredTagIds].every((id) => noteTagIds.has(id));
      });
    }

    // Exclude-tag filter: notes containing ANY of the excluded tags are removed.
    // This uses OR logic — a note is hidden if it has even one excluded tag.
    if (Array.isArray(filter.ExcludeTagIds) && filter.ExcludeTagIds.length > 0) {
      const excludedTagIds = new Set(filter.ExcludeTagIds.filter((id) => !!id));
      filteredData = filteredData.filter((note) => {
        const tags = Array.isArray(note.Tags) ? note.Tags : [];
        const noteTagIds = new Set(
          tags.map((tag) => (typeof tag === 'string' ? tag : String(tag._id ?? '')))
        );
        return ![...excludedTagIds].some((id) => noteTagIds.has(id));
      });
    }

    // Search filter
    if (filter.SearchString) {
      const rawSearchType = String(filter.SearchType ?? '').trim();
      const searchType = rawSearchType.toLowerCase();
      const searchText = filter.SearchString.toLowerCase().trim();

      const matchesTitle = (item: Note) => item.Title?.toLowerCase().includes(searchText);
      const matchesKeywords = (item: Note) =>
        (item.Keywords || '').toLowerCase().includes(searchText) ||
        (item.AiKeywords || '').toLowerCase().includes(searchText);
      const matchesTags = (item: Note) => FilterLogic.noteMatchesTagSearch(item, searchText, tagById);

      if (searchType === 'semantic') {
        // Semantic mode is handled in HomeComponent by calling the semantic search API.
      } else if (searchType === 'full text') {
        filteredData = filteredData.filter((item) => FilterLogic.noteMatchesFullText(item, searchText));
      } else if (searchType === 'title only') {
        filteredData = filteredData.filter((item) => matchesTitle(item));
      } else if (searchType === 'title & tags') {
        filteredData = filteredData.filter((item) => matchesTitle(item) || matchesTags(item));
      } else if (searchType === 'tags only') {
        filteredData = filteredData.filter((item) => matchesTags(item));
      } else if (
        searchType === 'title + keywords + tags' ||
        searchType === 'title, keywords & tags' ||
        searchType === 'title & keywords & tags'
      ) {
        filteredData = filteredData.filter((item) => matchesTitle(item) || matchesKeywords(item) || matchesTags(item));
      } else if (!searchType || searchType === 'title & keywords') {
        filteredData = filteredData.filter((item) => matchesTitle(item) || matchesKeywords(item));
      } else if (searchType === 'keywords only') {
        filteredData = filteredData.filter((item) => matchesKeywords(item));
      } else if (searchType === 'id') {
        filteredData = filteredData.filter((item) => (item._id || '').toLowerCase().includes(searchText));
      } else {
        // Unknown search type should never disable search completely.
        filteredData = filteredData.filter((item) => matchesTitle(item) || matchesKeywords(item) || matchesTags(item));
      }
    }

    // Type filter
    if (filter.Type) {
      filteredData = filteredData.filter((item) => item.NoteType && item.NoteType.includes(filter.Type!));
    }

    // Project filter
    if (filter.ProjectName) {
      const filterProject = filter.ProjectName.toLowerCase().trim();

      if (filterProject === '(not work)') {
        filteredData = filteredData.filter((item) => {
          const project = projects?.find((p) => p.ID === item.Project);
          const projectName = project?.Name?.toLowerCase().trim() || '';
          return !projectName.includes('work');
        });
      } else {
        filteredData = filteredData.filter((item) => {
          const project = projects?.find((p) => p.ID === item.Project);
          const projectName = project?.Name?.toLowerCase().trim() || '';
          return projectName.includes(filterProject);
        });
      }
    }

    // Status filter
    if (filter.Status && filter.Status !== '(All)') {
      if (filter.Status === '(All active)') {
        const activeValues = ['Current', 'Inbox', 'Next Action', 'Someday'];
        filteredData = filteredData.filter((item) => activeValues.some((status) => item.Status?.includes(status)));
      } else {
        filteredData = filteredData.filter((item) => item.Status && item.Status.includes(filter.Status!));
      }
    }

    // Category filter
    if (filter.Categories && filter.Categories.length > 0) {
      // Collect all selected categories + their children
      const expandedCategoryIds = new Set<string>();

      filter.Categories.forEach((categoryId) => {
        const cats = FilterLogic.getCategoryWithChildren(categoryId, categories);
        cats.forEach((c) => expandedCategoryIds.add(c._id));
      });

      // If selected categories are stale/invalid, treat as no category filter.
      if (expandedCategoryIds.size > 0) {
        // Filter notes if they have at least one matching category
        filteredData = filteredData.filter((item) => item.Categories?.some((cat) => expandedCategoryIds.has(cat)));
      }
    }

    // Modified date range filter
    const modifiedStart = filter.ModifiedBetween?.start;
    const modifiedEnd = filter.ModifiedBetween?.end;
    const hasModifiedBounds = FilterLogic.isValidDate(modifiedStart) || FilterLogic.isValidDate(modifiedEnd);
    if (hasModifiedBounds) {
      const start = FilterLogic.isValidDate(modifiedStart) ? modifiedStart : null;
      const end = FilterLogic.isValidDate(modifiedEnd) ? modifiedEnd : null;

      filteredData = filteredData.filter((item) => {
        // parse item's date safely
        const itemDate = item.last_modified ? new Date(item.last_modified) : null;
        if (!itemDate || isNaN(itemDate.getTime())) return false; // skip invalid

        let ok = true;

        if (start) {
          // exclude notes modified before start
          ok = ok && itemDate >= start;
        }
        if (end) {
          // exclude notes modified after end
          ok = ok && itemDate <= end;
        }

        return ok;
      });
    }

    // Created date range filter
    const createdStart = filter.CreatedBetween?.start;
    const createdEnd = filter.CreatedBetween?.end;
    const hasCreatedBounds = FilterLogic.isValidDate(createdStart) || FilterLogic.isValidDate(createdEnd);
    if (hasCreatedBounds) {
      const start = FilterLogic.isValidDate(createdStart) ? createdStart : null;
      const end = FilterLogic.isValidDate(createdEnd) ? createdEnd : null;

      filteredData = filteredData.filter((item) => {
        const itemDate = item.created_on ? new Date(item.created_on) : null;
        if (!itemDate || isNaN(itemDate.getTime())) return false;

        let ok = true;

        if (start) {
          ok = ok && itemDate >= start;
        }
        if (end) {
          ok = ok && itemDate <= end;
        }

        return ok;
      });
    }

    return filteredData;
  }
}
